import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DISPLAY_DEFAULTS } from '../utils/displayDefaults';
import { gradientPresets, ANIMATION_STYLES, PATTERN_STYLES } from '../utils/displayPresets';

const DEFAULT_TITLE_CONFIG = {
  text: 'Slope Wellness | Modality Lookup',
  color: '#FFFFFF',
  fontSize: '3rem',
};

const DEFAULT_LINK_TREE_CONFIG = {
  links: [
    { id: 1, label: 'Home', url: '#' },
    { id: 2, label: 'Our Services', url: '#' },
    { id: 3, label: 'Our Team', url: '#' },
    { id: 4, label: 'Memberships', url: '#' },
    { id: 5, label: 'Insurance Coverage', url: '#' },
  ],
  size: 240,
};

const DEFAULT_OVERVIEW_POSITION = {
  x: typeof window !== 'undefined' ? window.innerWidth - 400 : 600,
  y: 16,
};

// Owns the app's display / appearance state cluster:
//   * gradient + animation + pattern
//   * shift colors (bg + text, 4 shift types each)
//   * title + link-tree config, sop-button styling
//   * overview panel position (+ drag behavior)
//   * service-info font-size level
//   * isMuted + playClickSound (click audio depends on isMuted)
//
// The hook also owns:
//   * the debounced auto-save effect (admin-only) that fires onAutoSave()
//     whenever any tracked display value changes
//   * the overview-panel drag listeners + post-drop save
//   * hydrateFromServer(json): one-shot seeder invoked by the parent's
//     /api/data load. Internally flips settingsHydratedRef so the very
//     first auto-save cycle doesn't echo freshly loaded values back.
//
// Consumers receive:
//   * every state + setter (prop-drilled unchanged to AdminPanel etc.)
//   * selectedGradientCss / animationClassName / patternClassName
//   * playClickSound, getShiftStyles, getServiceInfoFontSizeClass,
//     increase/decreaseFontSize, handleDragMouseDown
//   * getSlice(): the display-owned subset of the /api/save payload.
//     NOTE: shiftColors is intentionally omitted to match the pre-refactor
//     getAllData() shape (pre-existing bug, tracked separately).
export function useDisplaySettings({ adminMode, onAutoSave }) {
  // Gradient / animation / pattern
  const [animationOpacity, setAnimationOpacity] = useState(DISPLAY_DEFAULTS.animationOpacity);
  const [animationSpeed, setAnimationSpeed] = useState(DISPLAY_DEFAULTS.animationSpeed);
  const [gradientPreset, setGradientPreset] = useState(DISPLAY_DEFAULTS.gradientPreset);
  const [customGradientColors, setCustomGradientColors] = useState(DISPLAY_DEFAULTS.customGradientColors);
  const [gradientAngle, setGradientAngle] = useState(DISPLAY_DEFAULTS.gradientAngle);
  const [animationStyle, setAnimationStyle] = useState(DISPLAY_DEFAULTS.animationStyle);
  const [patternStyle, setPatternStyle] = useState(DISPLAY_DEFAULTS.patternStyle);
  const [patternSpeed, setPatternSpeed] = useState(DISPLAY_DEFAULTS.patternSpeed);
  const [patternOpacity, setPatternOpacity] = useState(DISPLAY_DEFAULTS.patternOpacity);
  const [disableBackgroundEffects, setDisableBackgroundEffects] = useState(DISPLAY_DEFAULTS.disableBackgroundEffects);

  // Shift colors (NOT in getSlice() — matches current getAllData() shape.)
  const [amShiftBgColor, setAmShiftBgColor] = useState('#FDBA74');
  const [pmShiftBgColor, setPmShiftBgColor] = useState('#1E40AF');
  const [offShiftBgColor, setOffShiftBgColor] = useState('#9CA3AF');
  const [doubleShiftBgColor, setDoubleShiftBgColor] = useState('#FDBA74');
  const [amShiftTextColor, setAmShiftTextColor] = useState('#9A3403');
  const [pmShiftTextColor, setPmShiftTextColor] = useState('#DBEAFE');
  const [offShiftTextColor, setOffShiftTextColor] = useState('#374151');
  const [doubleShiftTextColor, setDoubleShiftTextColor] = useState('#FFFFFF');

  // Chrome / config
  const [titleConfig, setTitleConfig] = useState(DEFAULT_TITLE_CONFIG);
  const [linkTreeConfig, setLinkTreeConfig] = useState(DEFAULT_LINK_TREE_CONFIG);
  const [overviewPosition, setOverviewPosition] = useState(DEFAULT_OVERVIEW_POSITION);
  const [isMuted, setIsMuted] = useState(false);
  const [serviceInfoFontSizeLevel, setServiceInfoFontSizeLevel] = useState(1);
  const [sopButtonText, setSopButtonText] = useState('SOPs & Memos');
  const [sopButtonLink, setSopButtonLink] = useState('#');
  const [sopButtonBgColor, setSopButtonBgColor] = useState('#14B8A6');
  const [sopButtonTextColor, setSopButtonTextColor] = useState('#FFFFFF');

  // Overview drag transient state
  const [isOverviewDragging, setIsOverviewDragging] = useState(false);
  const [overviewDragStart, setOverviewDragStart] = useState({ x: 0, y: 0 });
  const overviewRef = useRef(null);

  // Refs
  // Tracks whether initial settings have been hydrated from Firestore.
  // Used to gate the auto-save effect so freshly loaded values don't
  // immediately re-save on first render.
  const settingsHydratedRef = useRef(false);
  const audioContextRef = useRef(null);
  // Signals "drop just happened, save after commit" so we can read
  // the fully committed overviewPosition inside onAutoSave's getAllData().
  const overviewDragJustEndedRef = useRef(false);

  // One-shot hydration from the /api/data response. Mirrors the sequence
  // of guarded setters that used to live inline in ModalityApp's load
  // effect. Deferred ref flip ensures the auto-save effect's first run
  // (which fires on the render following the setState batch) sees a
  // still-false hydrated ref and skips redundantly re-saving the values
  // we just loaded.
  const hydrateFromServer = useCallback((json) => {
    if (!json || typeof json !== 'object') return;

    if (typeof json.animationOpacity === 'number') setAnimationOpacity(json.animationOpacity);
    if (typeof json.animationSpeed === 'number') setAnimationSpeed(json.animationSpeed);
    if (typeof json.gradientPreset === 'string') setGradientPreset(json.gradientPreset);
    if (Array.isArray(json.customGradientColors)) {
      // Pad legacy 2-color palettes out to 4 stops so the editor always
      // has 4 swatches; repeat the last colour as filler so the visual
      // stays close to what was saved.
      const arr = json.customGradientColors.slice(0, 4);
      while (arr.length < 4) arr.push(arr[arr.length - 1] || '#314e4a');
      setCustomGradientColors(arr);
    }
    if (typeof json.gradientAngle === 'number') setGradientAngle(json.gradientAngle);
    if (typeof json.animationStyle === 'string' && ANIMATION_STYLES.some((s) => s.id === json.animationStyle)) {
      setAnimationStyle(json.animationStyle);
    }
    if (typeof json.patternStyle === 'string' && PATTERN_STYLES.some((p) => p.id === json.patternStyle)) {
      setPatternStyle(json.patternStyle);
    }
    if (typeof json.patternSpeed === 'number') setPatternSpeed(json.patternSpeed);
    if (typeof json.patternOpacity === 'number') setPatternOpacity(json.patternOpacity);
    if (typeof json.disableBackgroundEffects === 'boolean') setDisableBackgroundEffects(json.disableBackgroundEffects);
    if (typeof json.isMuted === 'boolean') setIsMuted(json.isMuted);

    setOverviewPosition(json.overviewPosition || DEFAULT_OVERVIEW_POSITION);
    setSopButtonText(json.sopButtonText || 'SOPs ->');
    setSopButtonLink(json.sopButtonLink || '#');
    setSopButtonBgColor(json.sopButtonBgColor || '#14B8A6');
    setSopButtonTextColor(json.sopButtonTextColor || '#FFFFFF');

    setTitleConfig(json.titleConfig || DEFAULT_TITLE_CONFIG);
    setLinkTreeConfig(json.linkTreeConfig || DEFAULT_LINK_TREE_CONFIG);

    if (json.shiftColors) {
      setAmShiftBgColor(json.shiftColors.amShiftBgColor || '#FDBA74');
      setPmShiftBgColor(json.shiftColors.pmShiftBgColor || '#1E40AF');
      setOffShiftBgColor(json.shiftColors.offShiftBgColor || '#9CA3AF');
      setDoubleShiftBgColor(json.shiftColors.doubleShiftBgColor || '#FDBA74');
      setAmShiftTextColor(json.shiftColors.amShiftTextColor || '#9A3403');
      setPmShiftTextColor(json.shiftColors.pmShiftTextColor || '#DBEAFE');
      setOffShiftTextColor(json.shiftColors.offShiftTextColor || '#374151');
      setDoubleShiftTextColor(json.shiftColors.doubleShiftTextColor || '#FFFFFF');
    } else {
      setAmShiftBgColor('#FDBA74');
      setPmShiftBgColor('#1E40AF');
      setOffShiftBgColor('#9CA3AF');
      setDoubleShiftBgColor('#FDBA74');
      setAmShiftTextColor('#9A3403');
      setPmShiftTextColor('#DBEAFE');
      setOffShiftTextColor('#374151');
      setDoubleShiftTextColor('#FFFFFF');
    }

    // Deferred to a macrotask so the auto-save effect's first run (next
    // render after this setState batch commits) still sees hydrated=false.
    setTimeout(() => { settingsHydratedRef.current = true; }, 0);
  }, []);

  // Debounced auto-save: fires onAutoSave() 700ms after any tracked
  // display value changes, only for admins. Settings used to only
  // persist as a side-effect of other save flows (editing a category
  // etc.), which meant the gradient/animation/pattern controls silently
  // reset on refresh if nothing else triggered a save. Exhaustive-deps
  // is disabled because onAutoSave is captured fresh each render.
  useEffect(() => {
    if (!settingsHydratedRef.current) return;
    if (!adminMode) return;
    const t = setTimeout(() => {
      onAutoSave();
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminMode,
    animationOpacity, animationSpeed,
    patternOpacity, patternSpeed,
    gradientPreset, customGradientColors,
    gradientAngle, animationStyle, patternStyle,
    disableBackgroundEffects,
  ]);

  // Overview panel drag behavior
  const handleDragMouseDown = useCallback((e) => {
    if (e.target.closest('button, a, input, select, textarea')) return;
    e.preventDefault();
    setIsOverviewDragging(true);
    setOverviewDragStart({ x: e.clientX - overviewPosition.x, y: e.clientY - overviewPosition.y });
  }, [overviewPosition.x, overviewPosition.y]);

  useEffect(() => {
    const handleDragMouseMove = (e) => {
      if (!isOverviewDragging || !overviewRef.current) return;
      let newX = e.clientX - overviewDragStart.x;
      let newY = e.clientY - overviewDragStart.y;
      const panelWidth = overviewRef.current.offsetWidth;
      const panelHeight = overviewRef.current.offsetHeight;
      newX = Math.max(0, Math.min(newX, window.innerWidth - panelWidth));
      newY = Math.max(0, Math.min(newY, window.innerHeight - panelHeight));
      setOverviewPosition({ x: newX, y: newY });
    };
    const handleDragMouseUp = () => {
      setIsOverviewDragging(false);
      // Position is already committed — flag a post-commit save so
      // onAutoSave() reads the fully committed overviewPosition via
      // getAllData() rather than a stale closure snapshot.
      overviewDragJustEndedRef.current = true;
    };
    if (isOverviewDragging) {
      document.addEventListener('mousemove', handleDragMouseMove);
      document.addEventListener('mouseup', handleDragMouseUp);
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleDragMouseMove);
      document.removeEventListener('mouseup', handleDragMouseUp);
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleDragMouseMove);
      document.removeEventListener('mouseup', handleDragMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isOverviewDragging, overviewDragStart]);

  // Fires after the drag-end render commits, so getAllData() inside
  // onAutoSave reads the final overviewPosition (matches pre-refactor
  // behavior where handleSave ran inside setOverviewPosition's updater).
  useEffect(() => {
    if (isOverviewDragging) return;
    if (!overviewDragJustEndedRef.current) return;
    overviewDragJustEndedRef.current = false;
    onAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverviewDragging, overviewPosition]);

  // Derived display values
  const activePaletteColors = useMemo(() => (
    gradientPreset === 'Custom'
      ? customGradientColors
      : (gradientPresets.find((g) => g.name === gradientPreset)?.colors || gradientPresets[0].colors)
  ), [gradientPreset, customGradientColors]);

  const selectedGradientCss = useMemo(
    () => `linear-gradient(${gradientAngle}deg, ${activePaletteColors.join(', ')})`,
    [gradientAngle, activePaletteColors],
  );

  const animationClassName = useMemo(
    () => (ANIMATION_STYLES.find((s) => s.id === animationStyle) || ANIMATION_STYLES[0]).className,
    [animationStyle],
  );

  const patternClassName = useMemo(
    () => (PATTERN_STYLES.find((p) => p.id === patternStyle) || PATTERN_STYLES[1]).className,
    [patternStyle],
  );

  // Click audio (depends on isMuted, so it lives with display settings)
  const triggerWebAudioClick = useCallback((audioCtx) => {
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1200, audioCtx.currentTime);
    gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.05);
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.05);
    oscillator.onended = () => { oscillator.disconnect(); gainNode.disconnect(); };
  }, []);

  const playClickSound = useCallback(() => {
    if (isMuted) return;
    if (typeof window === 'undefined' || (!window.AudioContext && !window.webkitAudioContext)) {
      console.warn('Web Audio API is not supported in this browser.');
      return;
    }
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioContextRef.current;
    if (audioCtx.state === 'suspended') {
      audioCtx.resume()
        .then(() => triggerWebAudioClick(audioCtx))
        .catch((e) => console.error('Error resuming AudioContext:', e));
      return;
    }
    if (audioCtx.state === 'running') triggerWebAudioClick(audioCtx);
  }, [isMuted, triggerWebAudioClick]);

  const getShiftStyles = useCallback((shiftType) => {
    switch (shiftType) {
      case 'AM': return { backgroundColor: amShiftBgColor, color: amShiftTextColor };
      case 'PM': return { backgroundColor: pmShiftBgColor, color: pmShiftTextColor };
      case 'DOUBLE': return { backgroundColor: doubleShiftBgColor, color: doubleShiftTextColor };
      case 'OFF': return { backgroundColor: offShiftBgColor, color: offShiftTextColor };
      default: return { backgroundColor: '#E5E7EB', color: '#1F2937' };
    }
  }, [
    amShiftBgColor, amShiftTextColor,
    pmShiftBgColor, pmShiftTextColor,
    doubleShiftBgColor, doubleShiftTextColor,
    offShiftBgColor, offShiftTextColor,
  ]);

  const getServiceInfoFontSizeClass = useCallback(() => {
    switch (serviceInfoFontSizeLevel) {
      case 0: return 'text-xs';
      case 1: return 'text-sm';
      case 2: return 'text-base';
      case 3: return 'text-lg';
      default: return 'text-sm';
    }
  }, [serviceInfoFontSizeLevel]);

  const increaseFontSize = useCallback(() => {
    playClickSound();
    setServiceInfoFontSizeLevel((prev) => Math.min(prev + 1, 3));
  }, [playClickSound]);

  const decreaseFontSize = useCallback(() => {
    playClickSound();
    setServiceInfoFontSizeLevel((prev) => Math.max(prev - 1, 0));
  }, [playClickSound]);

  // The display-owned subset of the /api/save payload.
  // Must match the pre-refactor getAllData() shape exactly — hence
  // shiftColors is NOT included (matches the current behavior where
  // shiftColors are persisted only via Firestore seed and get wiped
  // on every client save).
  const getSlice = useCallback(() => ({
    titleConfig,
    linkTreeConfig,
    animationOpacity,
    animationSpeed,
    gradientPreset,
    customGradientColors,
    gradientAngle,
    animationStyle,
    patternStyle,
    patternSpeed,
    patternOpacity,
    disableBackgroundEffects,
    isMuted,
    sopButtonText,
    sopButtonLink,
    sopButtonBgColor,
    sopButtonTextColor,
    overviewPosition,
  }), [
    titleConfig, linkTreeConfig,
    animationOpacity, animationSpeed,
    gradientPreset, customGradientColors, gradientAngle,
    animationStyle, patternStyle, patternSpeed, patternOpacity,
    disableBackgroundEffects,
    isMuted,
    sopButtonText, sopButtonLink, sopButtonBgColor, sopButtonTextColor,
    overviewPosition,
  ]);

  return {
    // Gradient / animation / pattern
    animationOpacity, setAnimationOpacity,
    animationSpeed, setAnimationSpeed,
    gradientPreset, setGradientPreset,
    customGradientColors, setCustomGradientColors,
    gradientAngle, setGradientAngle,
    animationStyle, setAnimationStyle,
    patternStyle, setPatternStyle,
    patternSpeed, setPatternSpeed,
    patternOpacity, setPatternOpacity,
    disableBackgroundEffects, setDisableBackgroundEffects,

    // Shift colors
    amShiftBgColor, setAmShiftBgColor,
    pmShiftBgColor, setPmShiftBgColor,
    offShiftBgColor, setOffShiftBgColor,
    doubleShiftBgColor, setDoubleShiftBgColor,
    amShiftTextColor, setAmShiftTextColor,
    pmShiftTextColor, setPmShiftTextColor,
    offShiftTextColor, setOffShiftTextColor,
    doubleShiftTextColor, setDoubleShiftTextColor,

    // Chrome / config
    titleConfig, setTitleConfig,
    linkTreeConfig, setLinkTreeConfig,
    overviewPosition, setOverviewPosition,
    isMuted, setIsMuted,
    serviceInfoFontSizeLevel, setServiceInfoFontSizeLevel,
    sopButtonText, setSopButtonText,
    sopButtonLink, setSopButtonLink,
    sopButtonBgColor, setSopButtonBgColor,
    sopButtonTextColor, setSopButtonTextColor,

    // Overview drag
    overviewRef,
    isOverviewDragging,
    handleDragMouseDown,

    // Derived
    selectedGradientCss,
    animationClassName,
    patternClassName,

    // Utilities
    playClickSound,
    getShiftStyles,
    getServiceInfoFontSizeClass,
    increaseFontSize,
    decreaseFontSize,

    // Payload wiring
    hydrateFromServer,
    getSlice,
  };
}
